/**
 * @file 划词「加入知识库」
 * 不用 useTextSelection（document mousedown 会强制清选区）。
 * document pointerup 取选区（拖选到容器外释放也能触发）；tip 打开后才听外部点击/滚动。
 */
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import UploadFileIcon from '@/pages/aigc/assets/svg/agent_add_file.svg';
import KnowledgeBaseAddModal from '../KnowledgeBaseAddModal';
import { getSelectionPayload } from './utils';
import './index.scss';

export interface ISelectionAddKbTipProps {
  /** 子元素 */
  children: React.ReactNode;
  /** 类名 */
  className?: string;
}

interface ITipState {
  /** 文本内容 */
  text: string;
  /** html 内容 */
  html: string;
  /** top */
  top: number;
  /** left */
  left: number;
}

const SelectionAddKbTip: React.FC<ISelectionAddKbTipProps> = ({
  children,
  className,
}) => {
  const { formatMessage } = useIntl();
  const contentRef = useRef<HTMLDivElement>(null);
  const kbModalVisibleRef = useRef(false);
  const tipOpenedAtRef = useRef(0);
  const pointerDownInHostRef = useRef(false);
  const [tip, setTip] = useState<ITipState | null>(null);
  const [modalContent, setModalContent] = useState('');
  const [modalContentHtml, setModalContentHtml] = useState('');
  const [kbModalVisible, setKbModalVisible] = useState(false);

  kbModalVisibleRef.current = kbModalVisible;
  const tipOpen = Boolean(tip) && !kbModalVisible;

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;

    let rafId = 0;

    const applySelectionTip = () => {
      if (kbModalVisibleRef.current) return;

      const payload = getSelectionPayload(el);
      if (!payload) {
        setTip((prev) => (prev ? null : prev));
        return;
      }

      const box = el.getBoundingClientRect();
      const nextTip: ITipState = {
        text: payload.text,
        html: payload.html,
        top: payload.rect.top - box.top,
        left: payload.rect.left - box.left + payload.rect.width / 2,
      };
      tipOpenedAtRef.current = Date.now();
      setTip((prev) =>
        prev &&
        prev.text === nextTip.text &&
        prev.html === nextTip.html &&
        prev.top === nextTip.top &&
        prev.left === nextTip.left
          ? prev
          : nextTip,
      );
    };

    const scheduleSelectionTip = () => {
      cancelAnimationFrame(rafId);
      // 双 rAF：等浏览器完成选区更新（Safari / 拖选跨元素时更稳）
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(applySelectionTip);
      });
    };

    const onPointerDown = (e: PointerEvent) => {
      pointerDownInHostRef.current = el.contains(e.target as Node);
    };

    const onPointerUp = (e: PointerEvent) => {
      if ((e.target as Element | null)?.closest?.('.dba-selection-kb-tip')) {
        return;
      }
      if (kbModalVisibleRef.current) return;

      // 拖选到容器外释放时 target 不在 host 内，但仍应识别 host 内选区
      if (!pointerDownInHostRef.current) {
        const sel = window.getSelection();
        const anchor = sel?.anchorNode;
        const anchorEl =
          anchor?.nodeType === Node.TEXT_NODE
            ? anchor.parentElement
            : (anchor as Element | null);
        if (!anchorEl || !el.contains(anchorEl)) {
          return;
        }
      }
      pointerDownInHostRef.current = false;
      scheduleSelectionTip();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('pointerup', onPointerUp, true);
    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('pointerup', onPointerUp, true);
    };
  }, []);

  useEffect(() => {
    if (!tipOpen) return undefined;

    const dismiss = () => setTip(null);
    const onMouseDown = (e: MouseEvent) => {
      if ((e.target as Element | null)?.closest?.('.dba-selection-kb-tip')) {
        return;
      }
      dismiss();
    };
    const onSelectionChange = () => {
      // 刚弹出 tip 时浏览器可能有一次空选区事件，忽略短暂窗口
      if (Date.now() - tipOpenedAtRef.current < 150) return;

      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !sel.toString().trim()) {
        dismiss();
      }
    };

    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('selectionchange', onSelectionChange);
    document.addEventListener('scroll', dismiss, {
      capture: true,
      passive: true,
      once: true,
    });
    return () => {
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('selectionchange', onSelectionChange);
      document.removeEventListener('scroll', dismiss, true);
    };
  }, [tipOpen]);

  const tipLabel = useMemo(
    () => formatMessage({ id: 'dba_kb_add_selection_btn' }),
    [formatMessage],
  );

  const handleTipMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!tip?.text) return;
    setModalContent(tip.text);
    setModalContentHtml(tip.html || '');
    setTip(null);
    setKbModalVisible(true);
    window.getSelection()?.removeAllRanges();
  };

  return (
    <>
      <div
        ref={contentRef}
        className={classNames('dba-selection-kb-tip-host', className)}
      >
        {children}
        {tipOpen && tip && (
          <button
            type="button"
            className="dba-selection-kb-tip"
            style={{ top: tip.top, left: tip.left }}
            onMouseDown={handleTipMouseDown}
          >
            <span className="dba-selection-kb-tip__inner">
              <img
                src={UploadFileIcon}
                alt=""
                className="dba-selection-kb-tip__icon"
              />
              <span className="dba-selection-kb-tip__label">{tipLabel}</span>
            </span>
          </button>
        )}
      </div>
      {kbModalVisible && (
        <KnowledgeBaseAddModal
          visible
          defaultContent={modalContent}
          defaultContentHtml={modalContentHtml}
          onClose={() => setKbModalVisible(false)}
        />
      )}
    </>
  );
};

export default memo(SelectionAddKbTip);
